import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { TribeId } from '@/content/xcloneHandles';
import { TRIBE_LABEL } from '@/content/xcloneHandles';

interface PowerStore {
  tribeRep: Record<TribeId, number>;
  notoriety: number;     // 0–100
  influence: number;     // 0–100
  bumpRep: (tribe: TribeId, delta: number) => void;
  bumpNotoriety: (delta: number) => void;
  bumpInfluence: (delta: number) => void;
}

const ZERO_REPS = (): Record<TribeId, number> => ({
  wsb: 0, fintwit: 0, cnbc: 0, maga: 0, left: 0, crypto: 0, press: 0, mom: 0,
});

export const usePower = create<PowerStore>()(
  immer((set) => ({
    tribeRep: ZERO_REPS(),
    notoriety: 0,
    influence: 0,
    bumpRep: (tribe, delta) => set((s) => {
      const before = s.tribeRep[tribe];
      const after = clamp(before + delta, -100, 100);
      s.tribeRep[tribe] = after;
      // Fire a milestone notification when crossing thresholds
      const thresholds = [-90, -50, -25, 25, 50, 90];
      for (const t of thresholds) {
        if ((before < t && after >= t) || (before > t && after <= t)) {
          queueMicrotask(() => fireRepMilestoneNotification(tribe, after, t));
        }
      }
    }),
    bumpNotoriety: (delta) => set((s) => {
      s.notoriety = clamp(s.notoriety + delta, 0, 100);
    }),
    bumpInfluence: (delta) => set((s) => {
      s.influence = clamp(s.influence + delta, 0, 100);
    }),
  })),
);

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

// Pulled out for lazy import — circular-ish dep with notifications + sounds
async function fireRepMilestoneNotification(tribe: TribeId, value: number, threshold: number): Promise<void> {
  const notifs = await import('./notifications');
  const sounds = await import('@/audio/synth');
  const tribeLabel = TRIBE_LABEL[tribe];
  let text: string;
  if (value >= 90) text = `${tribeLabel} adore you`;
  else if (value >= 50) text = `${tribeLabel} on your side`;
  else if (value >= 25) text = `${tribeLabel} starting to like you`;
  else if (value <= -90) text = `${tribeLabel} despise you`;
  else if (value <= -50) text = `${tribeLabel} actively against you`;
  else if (value <= -25) text = `${tribeLabel} souring on you`;
  else text = `${tribeLabel} rep crossed ${threshold}`;
  notifs.useNotifications.getState().push({
    surfaceId: 'x-clone',
    severity: Math.abs(value) >= 50 ? 'important' : 'info',
    text,
  });
  if (Math.abs(value) >= 50) sounds.sounds.notifyAlert();
}
